import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Loader2, FileSpreadsheet, History, Wrench } from 'lucide-react';
import { extractError } from '@/lib/errors';
import { loadRepairHistory, repairVersions, downloadRepairHistory } from '@/lib/tagRepairExcel';
import { BatchTagItem, buildTagDataFromDetail, computeDefaultLayout } from '@/components/jewellery/JewelleryTagViewer';
import { tagThemeClass } from './tagTheme';

function Version({ v }) {
  const data = useMemo(() => (v.det ? buildTagDataFromDetail(v.det) : null), [v.det]);
  return (
    <div className={`flex w-[min(400px,82vw)] shrink-0 flex-col overflow-hidden rounded-2xl border-2 ${v.post ? 'border-emerald-600/60' : 'border-slate-400/60'} bg-card`} data-testid={`tagws-repair-version-${v.key}`}>
      <div className={`px-4 py-2.5 text-white ${v.post ? 'bg-emerald-700' : 'bg-slate-800'}`}>
        <div className="flex items-center gap-1.5 text-sm font-semibold">{v.post ? <History size={14} /> : <Wrench size={14} />} {v.title}</div>
        <div className="mt-0.5 text-[11px] text-white/75" data-testid={`tagws-repair-version-${v.key}-date`}>{v.sub}</div>
      </div>
      <div className="grid flex-1 place-items-center overflow-x-auto bg-white p-3">
        {data ? <BatchTagItem data={data} layoutId={computeDefaultLayout(data, 'expanded')} rmMode="expanded" showQr={false} />
          : <p className="py-10 text-center text-xs text-slate-500">Details before this repair were not recorded.</p>}
      </div>
    </div>
  );
}

// Repair history: the tag as it was before each repair and after it, side by side; Excel on demand.
export function TagRepairHistoryDialog({ piece, onClose }) {
  const [hist, setHist] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { loadRepairHistory(piece.id).then(setHist).catch((e) => { toast.error(extractError(e, 'Could not load the repair history.')); onClose(); }); }, [piece.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const k = (e) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  const vers = hist ? repairVersions(hist) : [];
  const excel = async () => {
    setBusy(true);
    try { await downloadRepairHistory(piece.id, hist); toast.success('Repair history Excel downloaded.'); } catch (e) { toast.error(extractError(e, 'Could not build the Excel.')); } finally { setBusy(false); }
  };
  return createPortal(
    <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[320] flex items-end justify-center sm:items-center sm:p-4`} data-testid="tagws-repair-dialog">
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-3xl border border-border bg-card shadow-2xl animate-in fade-in zoom-in-95 duration-200 sm:max-w-6xl sm:rounded-3xl">
        <div className="flex items-center justify-between gap-3 border-b border-border bg-gradient-to-r from-slate-900 to-amber-800 px-5 py-4 text-white">
          <div className="min-w-0"><div className="flex min-w-0 items-center gap-2 font-semibold"><History size={17} className="shrink-0" /> <span className="truncate">Repair history · <span className="font-mono">{piece.jewellery_no}</span></span></div>
            <div className="text-xs text-white/70">{hist ? `${hist.repairs.length} repair${hist.repairs.length === 1 ? '' : 's'} · before and after each repair` : 'Loading…'}</div></div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={excel} disabled={!hist || busy} className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-white/15 px-3 text-xs font-semibold hover:bg-white/25 disabled:opacity-50" data-testid="tagws-repair-excel">{busy ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />} Excel</button>
            <button onClick={onClose} aria-label="Close" className="grid h-9 w-9 place-items-center rounded-xl hover:bg-white/10" data-testid="tagws-repair-close"><X size={18} /></button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-5">
          {!hist ? <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div> : (
            <div className="flex gap-5" data-testid="tagws-repair-versions">{vers.map((v) => <Version key={v.key} v={v} />)}</div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
